import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SubmittedOrder } from '@mypoz/shared';
import {
  PAY_PASSWORD_MAX_LENGTH,
  isValidPayPassword,
  orderPayableCents,
  walletShortfallCents,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Modal, Spinner } from '@mypoz/ui';

import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';
import { WALLET_BALANCE_ROUTE } from '@/shell/navigation.js';
import { useAccountSession } from '@/features/account/context.js';
import type { PromoDiscount } from '@/features/orders/api.js';
import { useOrders, type OrderPayOutcome } from '@/features/orders/context.js';
import { useWallet } from '@/features/wallet/index.js';

export interface PayFromWalletDialogProps {
  order: SubmittedOrder;
  onClose: () => void;
}

/** A label on the left, its figure hard against the right. */
function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className={strong ? 'text-ink' : 'text-muted'}>{label}</span>
      <span
        className={
          strong ? 'text-base font-semibold text-ink tabular-nums' : 'text-sm text-ink tabular-nums'
        }
      >
        {value}
      </span>
    </div>
  );
}

/** Outcomes shown on the entry screen, under the form. */
type EntryProblem = Extract<OrderPayOutcome, { kind: 'refused' | 'unconfirmed' }>;

/**
 * Paying an order out of the wallet, through `orders_pay` (MYP-92).
 *
 * Enter the payment password, wait for the vendor, then either done or the reason it did not happen, in one
 * dialog, so the amount about to leave the balance stays in view throughout.
 *
 * Nothing here decides whether the password is right or the money is there: the vendor answers both
 * (codes 2 and 3), and each has its own words. Nothing is changed locally either. After paying,
 * the balance is read again from the vendor before the done screen shows it, and the order is read
 * again when the dialog closes — not while it is open, because the card this dialog sits in may
 * move out of the current filter once the order is paid, and would take the dialog with it.
 *
 * A wallet short of the total cannot be topped up from here (decision A, MYP-92): no endpoint tops
 * up and pays in one step (missing-APIs §3 #17), so the dialog says how much is missing and points
 * at the Wallet page.
 */
export function PayFromWalletDialog({ order, onClose }: PayFromWalletDialogProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const wallet = useWallet();
  const { refresh: refreshAccount } = useAccountSession();
  const { pay, checkPromo, refreshOrder } = useOrders();

  const [password, setPassword] = useState('');
  // Q10: the password can be any text, so it is easy to mistype behind dots — the customer can look.
  const [showPassword, setShowPassword] = useState(false);
  const [stage, setStage] = useState<'entry' | 'pending' | 'done'>('entry');
  const [wrongPassword, setWrongPassword] = useState(false);
  const [problem, setProblem] = useState<EntryProblem | null>(null);
  /** Set when the vendor answered code 3, whatever the balance on screen said. */
  const [vendorSaysShort, setVendorSaysShort] = useState(false);
  /*
    MYP-95. A promo code for an order that has none yet, checked with `OrderCodenoCheck` against
    this order's real channel and billing weight. The discount is only ever the vendor's own
    answer, and typing after Apply drops it: what is sent is always what was checked.
  */
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<PromoDiscount | null>(null);
  const [promoProblem, setPromoProblem] = useState<'invalid' | 'failed' | null>(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  /** Whether the order may have changed at the vendor, so it is read again on close. */
  const sent = useRef(false);
  /*
    A second submit in the same tick (Enter pressed twice, a double click) sees the stage from
    before the first one re-rendered, so the stage cannot be the guard. This can.
  */
  const inFlight = useRef(false);

  /*
    The request cannot be cancelled once sent, so a dialog closed or unmounted mid-payment simply
    stops listening: nothing below touches state after that.
  */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const amountCents = orderPayableCents(order);
  /** What leaves the wallet: the total less a checked discount, never below zero. */
  const dueCents =
    amountCents === undefined ? undefined : Math.max(0, amountCents - (promo?.discountCents ?? 0));
  /** A code applies once: an order that already carries one gets no promo field (MYP-95). */
  const existingCode = order.couponCode.trim();
  const canAddPromo =
    existingCode === '' && order.channelId !== undefined && (order.chargeableWeightGrams ?? 0) > 0;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (code === '' || checkingPromo) return;
    setCheckingPromo(true);
    setPromoProblem(null);
    const outcome = await checkPromo(order.orderNumber, code);
    if (!mounted.current) return;
    setCheckingPromo(false);
    if (outcome.kind === 'valid') setPromo(outcome.promo);
    else setPromoProblem(outcome.kind === 'invalid' ? 'invalid' : 'failed');
  };

  /*
    Whether the wallet covered the order when the dialog opened. Frozen at mount: the balance is
    re-read after a payment, and a covered order that has just been paid must not flip to "short"
    on its own done screen.
  */
  const [shortAtOpen] = useState(() =>
    amountCents === undefined ? false : walletShortfallCents(wallet.balanceCents, amountCents) > 0,
  );

  const close = () => {
    if (sent.current) refreshOrder(order.orderNumber);
    onClose();
  };

  const submit = async () => {
    if (amountCents === undefined || !isValidPayPassword(password) || inFlight.current) return;

    inFlight.current = true;
    // Hidden again once sent: whatever comes back, the dialog should not keep it on screen.
    setShowPassword(false);
    setWrongPassword(false);
    setProblem(null);
    setStage('pending');
    sent.current = true;

    const outcome = await pay(order.orderNumber, password, promo ?? undefined);
    inFlight.current = false;
    if (!mounted.current) return;

    if (outcome.kind === 'paid' || (outcome.kind === 'unconfirmed' && outcome.nowPaid === true)) {
      // The balance the done screen shows is the vendor's, not a sum done here.
      await refreshAccount();
      if (!mounted.current) return;
      if (outcome.kind === 'unconfirmed') setProblem(outcome);
      setPassword('');
      setStage('done');
      return;
    }

    setStage('entry');
    if (outcome.kind === 'wrongPassword') {
      setPassword('');
      setWrongPassword(true);
    } else if (outcome.kind === 'insufficient') {
      setPassword('');
      setVendorSaysShort(true);
      void refreshAccount();
    } else if (outcome.kind === 'refused' || outcome.kind === 'unconfirmed') {
      setProblem(outcome);
    }
  };

  /*
    An unpriced order should never have offered a Pay button in the first place — there is nothing
    to pay without a price, so this stays a dead end.
  */
  if (amountCents === undefined) {
    return (
      <Modal title={t('orders.pay.title')} onClose={close}>
        <p className="text-sm text-ink">{t('orders.pay.notPriced')}</p>
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={close} className={buttonClass('secondary')}>
            {t('orders.pay.close')}
          </button>
        </div>
      </Modal>
    );
  }

  if (stage === 'done') {
    return (
      <Modal title={t('orders.pay.paidTitle')} onClose={close} focusKey="done">
        <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-4">
          <Icon name="check_circle" size={20} className="shrink-0 text-success" fill />
          <div>
            <p className="text-sm font-semibold text-ink">
              {t('orders.pay.paidBody', {
                number: order.orderNumber,
                amount: fmt.money(dueCents ?? amountCents, wallet.currency),
              })}
            </p>
            {problem?.kind === 'unconfirmed' ? (
              <p className="mt-1 text-sm text-ink">{t('orders.pay.unconfirmedPaid')}</p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 border-t border-line pt-3 text-sm">
          <Line
            label={t('orders.pay.newBalance')}
            value={fmt.money(wallet.balanceCents, wallet.currency)}
            strong
          />
        </div>

        <p className="mt-3 border-l-2 border-brand pl-3 text-sm text-ink">
          {t('orders.pay.paidNotice')}
        </p>

        <div className="mt-5 flex justify-end">
          <button type="button" onClick={close} className={buttonClass('primary', 'px-8')}>
            {t('orders.pay.done')}
          </button>
        </div>
      </Modal>
    );
  }

  if (shortAtOpen || vendorSaysShort) {
    const missing = walletShortfallCents(wallet.balanceCents, amountCents);
    return (
      <Modal title={t('orders.pay.title')} onClose={close} focusKey="short">
        <div className="border-b border-line pb-3">
          <Line label={order.orderNumber} value={fmt.money(amountCents, wallet.currency)} strong />
          <Line
            label={t('orders.pay.walletBalance')}
            value={fmt.money(wallet.balanceCents, wallet.currency)}
          />
        </div>

        <div role="alert" className="mt-4 rounded-md border border-warning/40 bg-warning/5 p-3">
          <p className="text-sm font-semibold text-ink">
            {missing > 0
              ? t('orders.pay.shortBy', { amount: fmt.money(missing, wallet.currency) })
              : t('orders.pay.shortVendor')}
          </p>
          <p className="mt-1 text-sm text-ink">{t('orders.pay.topUpFirst')}</p>
        </div>

        <AutoPayOffer order={order} />

        <div className="mt-5 flex items-center gap-4">
          <Link to={WALLET_BALANCE_ROUTE} onClick={close} className={buttonClass('primary')}>
            {t('orders.pay.goToWallet')}
          </Link>
          <button type="button" onClick={close} className={actionLinkClass()}>
            {t('orders.pay.close')}
          </button>
        </div>
      </Modal>
    );
  }

  /*
    A lost answer whose order could not be read either: it may have been paid. Offering Pay again
    could take the money twice, so the only way on is to close and look at the order.
  */
  const blocked = problem?.kind === 'unconfirmed' && problem.nowPaid === undefined;

  return (
    /*
      Undismissable while the payment is in flight: closing then would look like backing out of a
      payment the vendor may still make.
    */
    <Modal
      title={t('orders.pay.title')}
      onClose={close}
      dismissible={stage !== 'pending'}
      /*
        The stage itself, not a literal. Going pending disables every control in the panel at once,
        so focus has to be re-placed — onto the panel, since by then there is nothing else to land
        on. A fixed key here would leave focus on the button that just went disabled, which the
        browser resolves by dropping it to the body: outside the dialog, which is the one place it
        must not be.
      */
      focusKey={stage}
    >
      <div className="border-b border-line pb-3">
        <Line label={order.orderNumber} value={fmt.money(amountCents, wallet.currency)} strong />
        {promo ? (
          <>
            <Line
              label={t('orders.pay.promo.discount', { code: promo.code })}
              value={`−${fmt.money(promo.discountCents, wallet.currency)}`}
            />
            <Line
              label={t('orders.pay.promo.toPay')}
              value={fmt.money(dueCents ?? amountCents, wallet.currency)}
              strong
            />
          </>
        ) : null}
        {existingCode !== '' ? (
          <p className="py-1 text-sm text-muted">
            {t('orders.pay.promo.onOrder', { code: existingCode })}
          </p>
        ) : null}
        <Line
          label={t('orders.pay.walletBalance')}
          value={fmt.money(wallet.balanceCents, wallet.currency)}
        />
      </div>

      {canAddPromo ? (
        <div className="mt-4">
          <label htmlFor="pay-promo" className="mb-1.5 block text-sm text-muted">
            {t('orders.pay.promo.label')}
          </label>
          <div className="flex gap-2">
            <input
              id="pay-promo"
              type="text"
              autoComplete="off"
              value={promoInput}
              disabled={stage === 'pending' || checkingPromo}
              aria-invalid={promoProblem === 'invalid'}
              aria-describedby={promoProblem ? 'pay-promo-problem' : undefined}
              onChange={(event) => {
                setPromoInput(event.target.value);
                setPromo(null);
                setPromoProblem(null);
              }}
              placeholder={t('orders.pay.promo.placeholder')}
              className="min-h-11 w-full min-w-0 rounded-md border border-line bg-surface px-3 text-sm text-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60 lg:min-h-10"
            />
            <button
              type="button"
              onClick={() => void applyPromo()}
              disabled={
                promoInput.trim() === '' || promo !== null || checkingPromo || stage === 'pending'
              }
              className={buttonClass(
                'secondary',
                'shrink-0 disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {checkingPromo ? t('orders.pay.promo.checking') : t('orders.pay.promo.apply')}
            </button>
          </div>
          {promoProblem ? (
            <p id="pay-promo-problem" role="alert" className="mt-1.5 text-sm text-danger">
              {t(`orders.pay.promo.${promoProblem}`)}
            </p>
          ) : promo ? (
            <p className="mt-1.5 text-sm text-success">
              {t('orders.pay.promo.applied', {
                code: promo.code,
                amount: fmt.money(promo.discountCents, wallet.currency),
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="mt-4"
      >
        <label htmlFor="pay-password" className="mb-1.5 block text-sm text-muted">
          {t('orders.pay.passwordLabel')}
        </label>

        <div className="relative">
          {/*
            Masked until asked for, and never autocompleted: a payment password is not something a
            browser should keep. Any text up to the cap, sent exactly as typed — never trimmed.

            The eye inside the field shows and hides it. Edge draws its own reveal eye on password
            fields (`::-ms-reveal`); that one is hidden so there is only ever one eye, and ours
            works in every browser, not only while the field has focus.
          */}
          <input
            id="pay-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={PAY_PASSWORD_MAX_LENGTH}
            value={password}
            aria-invalid={wrongPassword}
            aria-describedby={wrongPassword ? 'pay-password-error' : 'pay-password-hint'}
            disabled={stage === 'pending' || blocked}
            onChange={(event) => {
              setPassword(event.target.value.slice(0, PAY_PASSWORD_MAX_LENGTH));
              setWrongPassword(false);
            }}
            className="min-h-12 w-full min-w-0 rounded-md border border-brand bg-surface py-2 pr-12 pl-3 text-base text-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60 [&::-ms-reveal]:hidden"
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            aria-pressed={showPassword}
            aria-controls="pay-password"
            aria-label={showPassword ? t('orders.pay.hidePassword') : t('orders.pay.showPassword')}
            disabled={stage === 'pending' || blocked}
            className="absolute top-1/2 right-0.5 flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60"
          >
            <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={20} />
          </button>
        </div>

        {wrongPassword ? (
          <p
            id="pay-password-error"
            role="alert"
            className="mt-1.5 text-sm font-semibold text-danger"
          >
            {t('orders.pay.passwordIncorrect')}
          </p>
        ) : (
          <p id="pay-password-hint" className="mt-1.5 text-sm text-muted">
            {t('orders.pay.passwordHint')}
          </p>
        )}

        {problem ? <PayProblem problem={problem} /> : null}

        <div className="mt-5 flex items-center gap-4">
          <button
            type="submit"
            disabled={!isValidPayPassword(password) || stage === 'pending' || blocked}
            className={buttonClass(
              'primary',
              'px-8 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {stage === 'pending' ? <Spinner size={18} /> : null}
            {stage === 'pending' ? t('orders.pay.paying') : t('orders.pay.pay')}
          </button>

          <button
            type="button"
            onClick={close}
            disabled={stage === 'pending'}
            className={actionLinkClass('disabled:opacity-50')}
          >
            {blocked ? t('orders.pay.close') : t('orders.pay.cancel')}
          </button>
        </div>

        <p className="mt-4 border-l-2 border-brand pl-3 text-sm text-ink">
          {t('orders.pay.notice')}
        </p>
      </form>
    </Modal>
  );
}

/** Why a payment did not happen, or why we cannot say. The vendor's own text goes beneath. */
function PayProblem({ problem }: { problem: EntryProblem }) {
  const { t } = useTranslation();

  if (problem.kind === 'unconfirmed') {
    return (
      <p
        role="alert"
        className="mt-3 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger"
      >
        {problem.nowPaid === false
          ? t('orders.pay.unconfirmedUnpaid')
          : t('orders.pay.unconfirmedUnknown')}
      </p>
    );
  }

  return (
    <div role="alert" className="mt-3 rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
      <p className="text-sm text-danger">{t('orders.pay.refused')}</p>
      <p className="mt-1 text-xs text-muted">
        {problem.detail !== ''
          ? t('orders.pay.vendorSaid', { detail: problem.detail })
          : t('orders.pay.code', { code: problem.code })}
      </p>
    </div>
  );
}

/**
 * "Pay automatically when I top up" (MYP-97), on the short-balance screen.
 *
 * Switches the order to AUTO through `ajaxUpdateUserOrderPayment`, so the vendor pays it by itself
 * once the wallet covers it, whichever way the customer tops up. Asked first, because nothing
 * switches it back to manual.
 *
 * The payment method is not on a summary row (MANUAL is assumed there), so the order's detail is
 * loaded first: an order already on AUTO is told so rather than offered the switch again.
 */
function AutoPayOffer({ order }: { order: SubmittedOrder }) {
  const { t } = useTranslation();
  const { loadDetail, detailStatus, setAutoPay } = useOrders();
  const [stage, setStage] = useState<'idle' | 'asking' | 'sending'>('idle');
  const [problem, setProblem] = useState<'refused' | 'notAuto' | 'unknown' | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    loadDetail(order.orderNumber);
  }, [loadDetail, order.orderNumber]);

  const detail = detailStatus(order.orderNumber);

  if (detail === 'ready' && order.paymentMode === 'AUTO') {
    return (
      <p role="status" className="mt-3 text-sm text-ink">
        {t('orders.pay.autoPay.isAuto')}
      </p>
    );
  }
  // Still finding out whether it is already automatic: offer nothing rather than guess.
  if (detail === 'idle' || detail === 'loading') return null;

  const confirm = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStage('sending');
    setProblem(null);
    const outcome = await setAutoPay(order.orderNumber);
    inFlight.current = false;
    if (!mounted.current) return;
    setStage('idle');
    if (outcome.kind === 'refused') setProblem('refused');
    else if (outcome.kind === 'unconfirmed' && outcome.nowAuto !== true) {
      setProblem(outcome.nowAuto === false ? 'notAuto' : 'unknown');
    }
  };

  return (
    <div className="mt-3">
      {stage === 'asking' ? (
        <div className="rounded-md border border-line p-3">
          <p className="text-sm text-ink">{t('orders.pay.autoPay.confirm')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => void confirm()} className={buttonClass('primary')}>
              {t('orders.pay.autoPay.confirmYes')}
            </button>
            <button type="button" onClick={() => setStage('idle')} className={actionLinkClass()}>
              {t('orders.pay.autoPay.confirmBack')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setProblem(null);
            setStage('asking');
          }}
          disabled={stage === 'sending'}
          className={buttonClass('secondary', 'disabled:cursor-not-allowed disabled:opacity-50')}
        >
          {stage === 'sending' ? t('orders.pay.autoPay.sending') : t('orders.pay.autoPay.offer')}
        </button>
      )}
      {problem ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {t(`orders.pay.autoPay.problem.${problem}`)}
        </p>
      ) : null}
    </div>
  );
}
